"use client"

import { useEffect, useRef, useState, type RefObject } from "react"
import Link from "next/link"
import Image from "next/image"
import { useMovingRail } from "@/hooks/use-moving-rail"
import { MovingRailControls } from "@/components/brand-kit/moving-rail-controls"
import type { FooterLiveData, FooterPopularProduct } from "@/app/api/chrome/footer-live/route"

/**
 * Footer live data (chrome-upgrade lane, zone 5).
 *
 * Fetches /api/chrome/footer-live ONCE and renders "Popular this week" — the
 * top TEN products by the real popularity_score in a sliding strip (owner
 * ruling 08/22/2026, FRIDGE-493), links only to gated, active PDPs.
 *
 * FRIDGE-1014 (owner screenshot, 09/11/2026): the trust-stat strip that used
 * to sit above this block ("6,000+ fridges and freezers tracked · N retailers
 * with live prices today · Price history since 2024") is REMOVED — the owner
 * struck it through and wrote "remove". The API still returns the two counts;
 * nothing here reads them any more. The same screenshot asked for "less
 * padding" between the "Popular this week" heading and its cards: the
 * heading and the arrow controls now share one row (the controls used to sit
 * on their own 52 px row under the heading), so the gap is the row's own
 * margin, not a stack of two blocks.
 *
 * The block self-hides when its data is absent (e.g. the API failed, or
 * popularity_score is unset), so the footer never shows a hollow or invented
 * figure. The footer stays a synchronous dual-tree component; this client
 * child keeps the rail current without threading async loaders through ~60
 * chrome render sites.
 */
export function FooterLive() {
  const [data, setData] = useState<FooterLiveData | null>(null)
  const railRef = useRef<HTMLUListElement | null>(null)

  useEffect(() => {
    let alive = true
    fetch("/api/chrome/footer-live")
      .then((r) => (r.ok ? r.json() : null))
      .then((d: FooterLiveData | null) => {
        if (alive && d) setData(d)
      })
      .catch(() => {
        /* non-fatal — blocks stay hidden */
      })
    return () => {
      alive = false
    }
  }, [])

  const motion = useMovingRail(railRef, data, -24)

  const popular: FooterPopularProduct[] = data?.popular ?? []

  return <PopularThisWeek popular={popular} railRef={railRef} move={motion.move} progressRef={motion.progressRef} />
}

/**
 * The presentational rail — exported so it can be rendered with explicit
 * data (tests, the isolated component harness) without the fetch.
 */
export function PopularThisWeek({ popular, railRef, move, progressRef }: {
  popular: FooterPopularProduct[]
  railRef: RefObject<HTMLUListElement | null>
  move: (direction: number) => void
  /** FRIDGE-1093: the position marker's fill, driven by the rail hook. */
  progressRef?: RefObject<HTMLElement | null>
}) {
  return (
    <>
      {popular.length > 0 && (
        <div>
          {/* TOP 10, SLIDING (owner ruling 08/22/2026, FRIDGE-493 — was a
              static 2x4 grid of 4). A scroll-snap strip with arrow buttons:
              the compact 56px cards stay exactly as QA'd on 07/20 (equal
              heights, reserved brand line, 2-line clamp); only the container
              moved from grid to a horizontal slider. Arrows scroll by one
              viewport; the strip is also touch/trackpad draggable.
              FRIDGE-1014: heading and controls share ONE row — centered
              heading, arrows at the right edge — so nothing sits between the
              heading and the cards but the row's own small margin. */}
          <div className="relative max-w-[900px] mx-auto">
            {/* Phone: heading left, arrows right (a centered heading would run
                into the arrows at 375 px). From sm up: symmetric 1fr/auto/1fr
                so the heading is truly centered with the arrows at the edge. */}
            <div className="mb-1 grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_auto_1fr] items-center gap-3">
              <span aria-hidden="true" className="hidden sm:block" />
              <h3 className="m-0 text-left sm:text-center font-semibold text-orange-500 text-sm uppercase tracking-wide">
                Popular this week
              </h3>
              <div className="justify-self-end">
                <MovingRailControls label="popular products" move={move} progressRef={progressRef} />
              </div>
            </div>
            <ul
              ref={railRef}
              className="flex gap-3 overflow-x-auto cursor-grab select-none pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden items-stretch"
            >
              {[false, true].flatMap((dup) => popular.map((p, i) => (
                <li
                  key={`${dup ? "dup-" : ""}${p.href}`}
                  data-rail-first={!dup && i === 0 || undefined}
                  data-rail-repeat={dup && i === 0 || undefined}
                  className="h-full w-[210px] shrink-0 snap-start"
                  // The second copy exists only so the drift can wrap
                  // seamlessly — screen readers and the tab order see ONE list.
                  aria-hidden={dup || undefined}
                >
                <Link
                  href={p.href}
                  // Named on the server (app/api/chrome/footer-live), brand and title
                  // first, then the FIN: two cards in this live-ranked rail can carry
                  // the same manufacturer title (WCAG 2.4.4, FRIDGE-796). Computing it
                  // there keeps the FIN parser out of every page's shared bundle.
                  aria-label={p.linkName || undefined}
                  tabIndex={dup ? -1 : undefined}
                  className="group flex h-full items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 hover:border-[#ff9951] hover:shadow-sm transition-all"
                >
                  <span className="relative w-14 h-14 shrink-0 rounded-lg bg-gray-50 overflow-hidden ring-1 ring-black/5">
                    {p.image ? (
                      <Image
                        src={p.image}
                        alt={p.title}
                        fill
                        sizes="56px"
                        className="object-contain p-1.5"
                        loading="lazy"
                      />
                    ) : null}
                  </span>
                  <span className="flex min-w-0 flex-col justify-center">
                    <span className="block h-[15px] leading-[15px] text-[11px] font-semibold uppercase tracking-wide text-[#0469f0] truncate">
                      {p.brand || ' '}
                    </span>
                    {/* NO `block` here — `.block { display:block }` cancels
                        line-clamp's `display:-webkit-box`, which is why titles
                        used to wrap 5-7 lines and rag the card heights (root
                        cause of the owner's screenshot). */}
                    <span className="mt-0.5 min-h-[2.25rem] text-[13px] leading-snug text-gray-800 line-clamp-2 group-hover:text-[#ff9951] transition-colors">
                      {p.title}
                    </span>
                  </span>
                </Link>
              </li>
              )))}
            </ul>
          </div>
        </div>
      )}
    </>
  )
}
